import React from 'react';

import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { FiAlertTriangle } from 'react-icons/fi';

import { TeamsReportRequest } from '../../../api/reports/teamsReports';
import { ReportStatusMark } from '../mark/ReportStatusMark';
import { useReportStyles } from '../styles';
import { formatReportSize } from '../utils';

interface Props {
  report: TeamsReportRequest | null;
  timedOut: boolean;
  /** Polling itself failed (e.g. the network dropped) — the report may still be generating. */
  pollError: boolean;
  notifying: boolean;
  /** The mark has turned into the check; until then a finished report still reads as generating. */
  landed: boolean;
  onLanded: () => void;
}

/** What the dialog shows once a report has been requested. */
export const ReportStatusPanel: React.FC<Props> = ({
  report,
  timedOut,
  pollError,
  notifying,
  landed,
  onLanded,
}) => {
  const classes = useReportStyles();
  const status = report?.status ?? 'pending';
  const isSnapshot = report?.report_type === 'company_snapshot';
  const withAnalysis = !!report && report.analysis_mode !== 'none';
  const noun = `${isSnapshot ? 'snapshot' : 'report'}${withAnalysis ? ' and analysis' : ''}`;

  if (status === 'failed' || status === 'expired' || pollError) {
    return (
      <Box className={classes.statusPanel}>
        <Box className={clsx(classes.statusIcon, classes.statusIconFailed)}>
          <FiAlertTriangle size={22} />
        </Box>
        <Typography className={classes.statusTitle}>
          {pollError ? 'We couldn’t check on your report' : 'We couldn’t generate this report'}
        </Typography>
        <Typography className={classes.hint}>
          {pollError
            ? 'It may still be on its way — check the Reports page in a minute.'
            : 'Something went wrong on our side. Try again, and if it keeps failing contact support'}
          {!pollError && report?.error_code ? ` quoting “${report.error_code}”.` : !pollError ? '.' : ''}
        </Typography>
      </Box>
    );
  }

  const ready = status === 'completed' && landed;

  return (
    <Box className={classes.statusPanel} aria-live="polite">
      {/* One mark for every in-progress state, so it can morph rather than be swapped */}
      <Box className={classes.statusMark}>
        <ReportStatusMark done={status === 'completed'} onLanded={onLanded} />
      </Box>

      {ready ? (
        <Box key="ready" className={classes.statusCopy}>
          <Typography className={classes.statusTitle}>Your {noun} is ready</Typography>
          <Typography className={classes.hint}>
            {[report?.page_count ? `${report.page_count} pages` : '', formatReportSize(report?.size_bytes)]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {report?.error_code === 'analysis_failed' && (
            <Typography className={classes.hint}>
              Analysis unavailable — the report is complete without it.
            </Typography>
          )}
          <Typography className={classes.hint}>
            Every report is logged for your organisation’s audit trail. The file is available for 30 days.
          </Typography>
        </Box>
      ) : (
        <Box key="working" className={classes.statusCopy}>
          <Typography className={classes.statusTitle}>
            {status === 'pending' ? `Your ${noun} is queued…` : `Generating your ${noun}…`}
          </Typography>
          <Typography className={classes.hint}>
            {timedOut
              ? 'Taking longer than expected — check the Reports page.'
              : `${
                  isSnapshot ? 'Adding up the numbers.' : 'Fetching profiles, qualifications and card images.'
                } This usually takes under a minute. You can close this window${
                  notifying ? ' — we’ll notify you when it’s ready.' : ' and find it on the Reports page.'
                }`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
